import {
  DEMOGRAPHIC_PANELS,
  EDUCATION_OPTIONS,
  EMPLOYMENT_OPTIONS,
  GENDER_OPTIONS,
  PANEL_CHECKBOX_OPTIONS,
  US_STATES,
} from './demographicFilterCatalog'
import type { PublishWizardStepNumber } from '@/features/instruments/publish/shared/lib/publishWizardSteps'
import type { ProfileAttributeFilter } from '@brushfire/types'
import type { AudienceFilters, DemographicPanelId } from './audienceTypes'
import { MIN_SIGNAL_PROVIDERS, VERY_LOW_MATCH_COUNT_THRESHOLD } from './audienceTypes'
import { normalizeDeviceTypeValue } from './deviceTypeFilter'
import { normalizeAudienceFiltersForCompare } from './audienceFilterNormalize'

const DEMOGRAPHIC_PANEL_ID_SET = new Set(DEMOGRAPHIC_PANELS.map((panel) => panel.id))

/** Guards API / draft payloads where `suggestedPanelIds` is missing or not an array. */
export function coerceDemographicPanelIds(value: unknown): DemographicPanelId[] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (id): id is DemographicPanelId =>
      typeof id === 'string' && DEMOGRAPHIC_PANEL_ID_SET.has(id as DemographicPanelId),
  )
}

export function asCompletedStepsSet(
  completedSteps: Set<PublishWizardStepNumber> | Iterable<PublishWizardStepNumber> | null | undefined,
): Set<PublishWizardStepNumber> {
  if (completedSteps instanceof Set) return completedSteps
  if (completedSteps == null) return new Set()
  return new Set(completedSteps)
}

function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  const obj = value as Record<string, unknown>
  const keys = Object.keys(obj).sort((a, b) => a.localeCompare(b))
  const entries = keys.map((k) => JSON.stringify(k) + ':' + stableStringify(obj[k]))
  return `{${entries.join(',')}}`
}

export function filtersDeepEqual(a: AudienceFilters, b: AudienceFilters): boolean {
  return (
    stableStringify(normalizeAudienceFiltersForCompare(a)) ===
    stableStringify(normalizeAudienceFiltersForCompare(b))
  )
}

/** Order-sensitive: the selected-filter list is a sequence the researcher can reorder. */
export function profileAttributeFiltersEqual(
  a: readonly ProfileAttributeFilter[],
  b: readonly ProfileAttributeFilter[],
): boolean {
  if (a.length !== b.length) return false
  return a.every((filter, index) => {
    const other = b[index]
    if (!other) return false
    if (filter.attributeId !== other.attributeId) return false
    if ((filter.systemKey ?? null) !== (other.systemKey ?? null)) return false
    if (filter.values.length !== other.values.length) return false
    return filter.values.every((value, valueIndex) => value === other.values[valueIndex])
  })
}

function filtersDifferForPanel(
  panelId: DemographicPanelId,
  a: AudienceFilters,
  b: AudienceFilters,
): boolean {
  const left = normalizeAudienceFiltersForCompare(a)
  const right = normalizeAudienceFiltersForCompare(b)
  switch (panelId) {
    case 'ageRange':
      return stableStringify(left.ageRange) !== stableStringify(right.ageRange)
    case 'gender':
      return stableStringify(left.gender) !== stableStringify(right.gender)
    case 'location':
      return stableStringify(left.location) !== stableStringify(right.location)
    case 'education':
      return stableStringify(left.education) !== stableStringify(right.education)
    case 'employment':
      return stableStringify(left.employment) !== stableStringify(right.employment)
    case 'deviceType':
      return (
        stableStringify(normalizeDeviceTypeValue(left.deviceType)) !==
        stableStringify(normalizeDeviceTypeValue(right.deviceType))
      )
    case 'maritalStatus':
      return stableStringify(left.maritalStatus) !== stableStringify(right.maritalStatus)
    case 'householdSize':
      return stableStringify(left.householdSize) !== stableStringify(right.householdSize)
    case 'numberOfChildren':
      return stableStringify(left.numberOfChildren) !== stableStringify(right.numberOfChildren)
    case 'industry':
      return stableStringify(left.industry) !== stableStringify(right.industry)
    case 'housingStatus':
      return stableStringify(left.housingStatus) !== stableStringify(right.housingStatus)
    case 'senateDistrict':
      return (left.senateDistrict ?? '') !== (right.senateDistrict ?? '')
    case 'registeredToVote':
      return stableStringify(left.registeredToVote) !== stableStringify(right.registeredToVote)
    case 'currentVoterRegistration':
      return (
        stableStringify(left.currentVoterRegistration) !==
        stableStringify(right.currentVoterRegistration)
      )
    default:
      return false
  }
}

/** Senate District / eKYC — researcher-authored, never copied from AI suggest. */
export function researcherAuthoredAudienceFilters(filters: AudienceFilters): AudienceFilters {
  const next: AudienceFilters = {}
  const senateDistrict = filters.senateDistrict?.trim()
  if (senateDistrict) next.senateDistrict = senateDistrict
  if (filters.ekycStatus && filters.ekycStatus !== 'all') next.ekycStatus = filters.ekycStatus
  if (filters.registeredToVote?.length) next.registeredToVote = [...filters.registeredToVote]
  if (filters.currentVoterRegistration?.length) {
    next.currentVoterRegistration = [...filters.currentVoterRegistration]
  }
  return next
}

const AI_SELECTABLE_PANELS: DemographicPanelId[] = [
  'ageRange',
  'gender',
  'location',
  'education',
  'employment',
  'deviceType',
  'maritalStatus',
  'householdSize',
  'numberOfChildren',
  'industry',
  'housingStatus',
]

function assignPanelValue(
  target: AudienceFilters,
  source: AudienceFilters,
  panelId: DemographicPanelId,
): void {
  switch (panelId) {
    case 'ageRange':
      if (source.ageRange) target.ageRange = { ...source.ageRange }
      return
    case 'location':
      if (source.location) target.location = { ...source.location }
      return
    case 'senateDistrict':
      return
    default: {
      const value = source[panelId]
      if (Array.isArray(value) && value.length > 0) {
        Object.assign(target, { [panelId]: [...value] })
      }
    }
  }
}

/**
 * BFP-252 — AI suggest must not pre-select Age / Device Type / other demographics.
 * Dimensions that still match the frozen AI suggestion stay unselected until Restore
 * (`aiSuggestionsApplied`) or the researcher edits that panel to a different value.
 */
export function activeFiltersWithoutUnappliedAi(input: {
  filters: AudienceFilters
  filtersModified?: boolean
  aiSuggestedFilters?: AudienceFilters
  aiSuggestionsApplied?: boolean
}): AudienceFilters {
  if (input.aiSuggestionsApplied === true) return input.filters
  const ai = input.aiSuggestedFilters
  if (!ai) {
    return input.filtersModified === true
      ? input.filters
      : researcherAuthoredAudienceFilters(input.filters)
  }
  const next = researcherAuthoredAudienceFilters(input.filters)
  for (const panelId of AI_SELECTABLE_PANELS) {
    if (filtersDifferForPanel(panelId, input.filters, ai)) {
      assignPanelValue(next, input.filters, panelId)
    }
  }
  return next
}

/** Panels where current filters differ from the frozen AI suggestion. */
export function computeUserModifiedPanelIds(
  filters: AudienceFilters,
  aiSuggestedFilters: AudienceFilters,
  suggestedPanelIds: DemographicPanelId[],
): DemographicPanelId[] {
  const fromSuggested = suggestedPanelIds.filter((panelId) =>
    filtersDifferForPanel(panelId, filters, aiSuggestedFilters),
  )
  // Senate District is researcher-authored and often omitted from AI suggestedPanelIds.
  if (
    !fromSuggested.includes('senateDistrict') &&
    filtersDifferForPanel('senateDistrict', filters, aiSuggestedFilters)
  ) {
    return [...fromSuggested, 'senateDistrict']
  }
  return fromSuggested
}

export function isPanelAiSuggested(
  panelId: DemographicPanelId,
  suggestedPanelIds: DemographicPanelId[] | undefined,
  filters: AudienceFilters,
  aiSuggestedFilters: AudienceFilters,
  userModifiedPanelIds: DemographicPanelId[] | undefined,
): boolean {
  const panels = coerceDemographicPanelIds(suggestedPanelIds)
  const modified = coerceDemographicPanelIds(userModifiedPanelIds)
  if (!panels.includes(panelId)) return false
  if (modified.includes(panelId)) return false
  return !filtersDifferForPanel(panelId, filters, aiSuggestedFilters)
}

export function hasDeviatedFromAiSuggestedFilters(
  filters: AudienceFilters,
  aiSuggestedFilters: AudienceFilters,
): boolean {
  return !filtersDeepEqual(filters, aiSuggestedFilters)
}

export function canAdvanceFromTargetAudience(input: {
  hasSenateDistrict: boolean
  isFutureAudienceMode: boolean
  /** BFP-1141 — All Registered Users: no filters, no match-count gate. */
  isAllRegisteredMode?: boolean
  matchCount: number | null | undefined
}): boolean {
  if (input.isAllRegisteredMode) return true
  // Existing responders only: Senate District is optional. Future matching still requires it.
  if (input.isFutureAudienceMode) return input.hasSenateDistrict
  return (input.matchCount ?? 0) >= MIN_SIGNAL_PROVIDERS
}

export function targetAudienceClearRestoreLabel(input: {
  isQuickInstrument: boolean
  isFutureAudienceMode: boolean
  isAllRegisteredMode?: boolean
  filters: AudienceFilters
  aiSuggestedFilters: AudienceFilters
}): string {
  if (input.isQuickInstrument || input.isFutureAudienceMode || input.isAllRegisteredMode) {
    return 'Clear All Filters'
  }
  if (hasDeviatedFromAiSuggestedFilters(input.filters, input.aiSuggestedFilters)) {
    return 'Restore AI Suggestions'
  }
  return 'Clear All Filters'
}

function labelForOption(
  options: { value: string; label: string }[],
  value: string,
): string {
  return options.find((o) => o.value === value)?.label ?? value
}

export type PersonaAvatarVariant = 'female' | 'male' | 'other'

/** Persona portrait — Figma `4687:4505` (female), `4687:4515` (male), `5388:9639` (other). */
export function resolvePersonaAvatarVariant(gender: string[] | undefined): PersonaAvatarVariant {
  if (gender?.length === 1) {
    if (gender[0] === 'female') return 'female'
    if (gender[0] === 'male') return 'male'
  }
  return 'other'
}

export interface PersonaDisplay {
  ageLabel: string | null
  regionLabel: string | null
  tagLabel: string | null
  avatarVariant: PersonaAvatarVariant
}

/** Structured persona lines for the Figma summary card (`4687:5532`). */
export function buildPersonaDisplay(filters: AudienceFilters): PersonaDisplay {
  let ageLabel: string | null = null
  if (filters.ageRange?.min != null && filters.ageRange?.max != null) {
    ageLabel = `${filters.ageRange.min}-${filters.ageRange.max} Yr`
  }

  let regionLabel: string | null = null
  if (filters.location?.state) {
    regionLabel = labelForOption(US_STATES, filters.location.state)
  }

  let tagLabel: string | null = null
  if (filters.employment?.length) {
    tagLabel = filters.employment
      .map((value: string) => labelForOption(EMPLOYMENT_OPTIONS, value))
      .join(', ')
  } else if (filters.education?.length) {
    tagLabel = labelForOption(EDUCATION_OPTIONS, filters.education[0])
  } else if (filters.gender?.length === 1) {
    tagLabel = labelForOption(GENDER_OPTIONS, filters.gender[0])
  }

  return {
    ageLabel,
    regionLabel,
    tagLabel,
    avatarVariant: resolvePersonaAvatarVariant(filters.gender),
  }
}

export type MatchCountFootnoteVariant = 'warning' | 'error'

/** Footnote under the match line — Figma `4687:5553` (separate from refresh hint `4687:5547`). */
export function getMatchCountFootnote(count: number): {
  variant: MatchCountFootnoteVariant
  text: string
} | null {
  if (count === 0) {
    return {
      variant: 'error',
      text: 'No Signal Providers match your current filters. Please broaden your filters.',
    }
  }
  if (count > 0 && count < VERY_LOW_MATCH_COUNT_THRESHOLD) {
    return {
      variant: 'warning',
      text: 'Very few Signal Providers match. Consider broadening your filters.',
    }
  }
  return null
}

function formatAgeRangeSummary(filters: AudienceFilters): string | null {
  const { min, max } = filters.ageRange ?? {}
  if (min == null && max == null) return null
  if (min != null && max != null) return `${min}–${max}`
  if (min != null) return `Min ${min}`
  return `Max ${max}`
}

function formatLocationSummary(filters: AudienceFilters): string | null {
  if (!filters.location?.state) return null
  const state = labelForOption(US_STATES, filters.location.state)
  if (filters.location.city) return `${state}, ${filters.location.city}`
  return state
}

function formatDeviceTypeSummary(deviceType: unknown): string | null {
  const channels = normalizeDeviceTypeValue(deviceType)
  if (!channels?.length) return null
  if (channels.length === 2) return 'Both'
  if (channels[0] === 'mobile') return 'Mobile'
  if (channels[0] === 'web') return 'Web'
  return null
}

export function formatPanelHeaderSummary(
  panelId: DemographicPanelId,
  filters: AudienceFilters,
): string | null {
  switch (panelId) {
    case 'ageRange':
      return formatAgeRangeSummary(filters)
    case 'gender':
      return formatCheckboxSummary(filters.gender, GENDER_OPTIONS)
    case 'location':
      return formatLocationSummary(filters)
    case 'education':
      return formatCheckboxSummary(filters.education, EDUCATION_OPTIONS)
    case 'employment':
      return formatCheckboxSummary(filters.employment, EMPLOYMENT_OPTIONS)
    case 'deviceType':
      return formatDeviceTypeSummary(filters.deviceType)
    case 'maritalStatus':
      return formatCheckboxSummary(filters.maritalStatus, PANEL_CHECKBOX_OPTIONS.maritalStatus)
    case 'householdSize':
      return formatCheckboxSummary(filters.householdSize, PANEL_CHECKBOX_OPTIONS.householdSize)
    case 'numberOfChildren':
      return formatCheckboxSummary(filters.numberOfChildren, PANEL_CHECKBOX_OPTIONS.numberOfChildren)
    case 'industry':
      return formatCheckboxSummary(filters.industry, PANEL_CHECKBOX_OPTIONS.industry, 1)
    case 'housingStatus':
      return formatCheckboxSummary(filters.housingStatus, PANEL_CHECKBOX_OPTIONS.housingStatus)
    case 'senateDistrict':
      return filters.senateDistrict?.trim() || null
    case 'registeredToVote':
      return formatCheckboxSummary(filters.registeredToVote, PANEL_CHECKBOX_OPTIONS.registeredToVote)
    case 'currentVoterRegistration':
      return formatCheckboxSummary(
        filters.currentVoterRegistration,
        PANEL_CHECKBOX_OPTIONS.currentVoterRegistration,
      )
    default:
      return null
  }
}

function formatCheckboxSummary(
  values: string[] | undefined,
  options: { value: string; label: string }[],
  maxLabels = 2,
): string | null {
  if (!values?.length) return null
  const labels = values.slice(0, maxLabels).map((v) => labelForOption(options, v))
  const suffix =
    values.length > maxLabels ? ` (+${values.length - maxLabels} more)` : ''
  return labels.join(', ') + suffix
}

export function countActiveFilterDimensions(filters: AudienceFilters): number {
  let n = 0
  if (filters.ageRange?.min != null || filters.ageRange?.max != null) n++
  if (filters.gender?.length) n++
  if (filters.location?.state) n++
  if (filters.education?.length) n++
  if (filters.employment?.length) n++
  if (normalizeDeviceTypeValue(filters.deviceType)?.length) n++
  if (filters.maritalStatus?.length) n++
  if (filters.householdSize?.length) n++
  if (filters.numberOfChildren?.length) n++
  if (filters.industry?.length) n++
  if (filters.housingStatus?.length) n++
  if (filters.senateDistrict?.trim()) n++
  if (filters.registeredToVote?.length) n++
  if (filters.currentVoterRegistration?.length) n++
  if (filters.ekycStatus && filters.ekycStatus !== 'all') n++
  return n
}
